import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  apiGetTimekeepingTasks, apiCreateTimekeepingTask, apiUpdateTimekeepingTask, apiDeleteTimekeepingTask,
  type TimekeepingTask,
} from "@/lib/api";

const PALETTE = ["#3b82f6", "#8b5cf6", "#ec4899", "#f97316", "#10b981", "#eab308", "#06b6d4", "#64748b"];

export function TaskManager() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<TimekeepingTask | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: PALETTE[0], projectCode: "", billable: false });

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["timekeeping-tasks-admin"],
    queryFn: () => apiGetTimekeepingTasks(true),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["timekeeping-tasks-admin"] });
    qc.invalidateQueries({ queryKey: ["timekeeping-tasks"] });
  };

  const createMut = useMutation({
    mutationFn: () => apiCreateTimekeepingTask(form),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(t("timekeeping.tasks.created", { defaultValue: "Task created" })); },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });
  const updateMut = useMutation({
    mutationFn: () => apiUpdateTimekeepingTask(editing!.id, form),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(t("timekeeping.tasks.updated", { defaultValue: "Task updated" })); },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });
  const toggleMut = useMutation({
    mutationFn: (task: TimekeepingTask) => apiUpdateTimekeepingTask(task.id, { isActive: !task.is_active }),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => apiDeleteTimekeepingTask(id),
    onSuccess: () => { invalidate(); toast.success(t("timekeeping.tasks.deleted", { defaultValue: "Task removed" })); },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", color: PALETTE[0], projectCode: "", billable: false });
    setOpen(true);
  };
  const openEdit = (task: TimekeepingTask) => {
    setEditing(task);
    setForm({
      name: task.name, description: task.description ?? "", color: task.color ?? PALETTE[0],
      projectCode: task.project_code ?? "", billable: task.billable,
    });
    setOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {t("timekeeping.tasks.subtitle", { defaultValue: "Define the tasks and projects employees can log time against." })}
        </p>
        <Button size="sm" onClick={openCreate} className="gradient-brand text-primary-foreground">
          <Plus className="h-4 w-4 mr-1" />{t("timekeeping.tasks.new", { defaultValue: "New task" })}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-left">
                <th className="px-4 py-2 font-medium">{t("timekeeping.tasks.name", { defaultValue: "Name" })}</th>
                <th className="px-4 py-2 font-medium">{t("timekeeping.tasks.project", { defaultValue: "Project" })}</th>
                <th className="px-4 py-2 font-medium">{t("timekeeping.tasks.billable", { defaultValue: "Billable" })}</th>
                <th className="px-4 py-2 font-medium">{t("timekeeping.tasks.active", { defaultValue: "Active" })}</th>
                <th className="px-4 py-2 w-24" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">…</td></tr>
              )}
              {!isLoading && tasks.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  {t("timekeeping.tasks.empty", { defaultValue: "No tasks yet." })}
                </td></tr>
              )}
              {tasks.map((task) => (
                <tr key={task.id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: task.color ?? "#94a3b8" }} />
                      {task.name}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{task.project_code || "—"}</td>
                  <td className="px-4 py-2">{task.billable ? <Badge variant="secondary">✓</Badge> : "—"}</td>
                  <td className="px-4 py-2">
                    <Switch checked={task.is_active} onCheckedChange={() => toggleMut.mutate(task)} />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(task)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteMut.mutate(task.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t("timekeeping.tasks.edit", { defaultValue: "Edit task" }) : t("timekeeping.tasks.new", { defaultValue: "New task" })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>{t("timekeeping.tasks.name", { defaultValue: "Name" })}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("timekeeping.tasks.descriptionLabel", { defaultValue: "Description" })}</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("timekeeping.tasks.project", { defaultValue: "Project" })}</Label>
              <Input value={form.projectCode} onChange={(e) => setForm({ ...form, projectCode: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("timekeeping.tasks.color", { defaultValue: "Color" })}</Label>
              <div className="flex gap-2 mt-1">
                {PALETTE.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                    className={`h-6 w-6 rounded-full border-2 ${form.color === c ? "border-foreground" : "border-transparent"}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.billable} onCheckedChange={(v) => setForm({ ...form, billable: v })} />
              <Label>{t("timekeeping.tasks.billable", { defaultValue: "Billable" })}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel", { defaultValue: "Cancel" })}</Button>
            <Button
              disabled={!form.name.trim() || createMut.isPending || updateMut.isPending}
              onClick={() => (editing ? updateMut.mutate() : createMut.mutate())}
            >
              {t("common.save", { defaultValue: "Save" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
